/**
 * Wolf tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const wolfTattoosPack: ToilePack = {
    slug: 'wolf-tattoos',
    category: 'tattoo',
    name: 'Wolf tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'howling', label: 'Howling wolf', subject: 'a wolf sitting on a rock with its head raised, howling at a full moon behind it'},
        {key: 'head', label: 'Wolf head', subject: "a wolf's head looking straight ahead, ears up, the fur around the cheeks and the eyes drawn sharply"},
        {key: 'forest', label: 'Wolf and forest', subject: "a wolf's head whose lower half becomes a line of pine trees, a small moon above its brow"},
    ],
    styles: [
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin even outline with every shadow built from stippled dots gathering into soft gradients', palette: 'black ink only', framing: 'the design centered with a white margin, the densest dots in the deepest folds'},
        {key: 'geometric', label: 'Geometric', style: 'a geometric tattoo drawing, the subject built from ruled lines and compassed arcs inside a framing shape, one uniform thin stroke', palette: 'black ink only', framing: 'the framing shape centered with an even white margin around it'},
        {key: 'fineline', label: 'Fine line', style: 'a fine line tattoo drawing, a single-needle hairline outline with delicate detail and no shading', palette: 'black ink only', framing: 'the design centered and light on the sheet, a wide white margin all round'},
    ],
    directions: ['a fox', 'a bear', 'red ink'],
    directionHint: 'try: a fox',
    seo: {
        title: 'Wolf tattoo designs: howling wolf, wolf head, wolf forest',
        metaDescription: "Nine wolf tattoo designs: a wolf howling at the full moon, a wolf's head and a wolf framed by a forest, in three styles.",
        h1: 'Wolf tattoo designs: howling wolf, wolf head, wolf and forest',
        tagline: 'Three wolves, each drawn in dotwork, geometric and fine line on white paper.',
        intro: "Wolf tattoo designs, worn for loyalty, family and the lone path, on the forearm, the calf or the chest. The nine sheets show a wolf on a rock howling at a full moon, a wolf's head looking straight out, and a wolf whose lower face becomes pine trees. Each is drawn as dotwork, geometric and fine line. Type a different animal or twist, such as a fox or a bear, and the same styles can be asked for.",
        useCases: [
            'a calf or forearm piece with a howling wolf',
            "a chest design with a wolf's head",
            'a forest scene for a larger back piece',
            'a printed sheet to compare styles before booking',
        ],
        faq: [
            {q: 'What is on the page?', a: "Nine wolf designs: a howling wolf, a wolf's head and a wolf framed by a forest, each in three styles."},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for a fox or a bear?', a: 'Yes. Type a different animal or twist, such as a fox or a bear; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Dotwork builds the shadows from dots. Geometric uses ruled lines and compassed arcs. Fine line is one thin outline with little shading.'},
        ],
    },
}
